import { ComboboxItem, MultiSelect } from '@mantine/core';
import { useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';

import { FormControlInput } from '@/core/components';

interface NamedOption {
  id: number;
  name: string;
}

export interface AsyncMultiSelectControlProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;
  description?: string;
  placeholder?: string;

  isDisabled?: boolean;

  options: NamedOption[];

  isLoading: boolean;
  isError: boolean;
}

function normalize(op: NamedOption): ComboboxItem {
  return {
    value: String(op.id),
    label: op.name,
  };
}

function filter(options: NamedOption[], ids: string[]): NamedOption[] {
  return options.filter(op => ids.indexOf(String(op.id)) !== -1);
}

export function AsyncMultiSelectControl<T extends FieldValues>(props: AsyncMultiSelectControlProps<T>) {
  const { withAsterisk, label, placeholder, description, name, isSubmitting, isDisabled, options, isError, isLoading } =
    props;
  const {
    field,
    fieldState: { error },
  } = useController({ name });

  const normalizedOptions = useMemo(() => (options ?? []).map(normalize), [options]);

  const normalizedOnChange = (ids: string[]) => {
    field.onChange(filter(options, ids));
  };

  const normalizedValue = field.value != null ? field.value.map((v: NamedOption) => String(v.id)) : null;

  return (
    <MultiSelect
      data={normalizedOptions}
      ref={field.ref}
      name={field.name}
      value={normalizedValue ?? []}
      onChange={normalizedOnChange}
      onDropdownClose={field.onBlur}
      label={label}
      description={description}
      placeholder={placeholder ?? label}
      withAsterisk={withAsterisk}
      error={error?.message ?? (isError ? 'Server error' : null)}
      disabled={isSubmitting || isDisabled || isLoading}
      searchable
      flex={1}
      withScrollArea={false}
      styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
    />
  );
}
